// Horizontal-scroll bookkeeping for tables with pinned (sticky) columns.
//
// `edge.start` / `edge.end` gate the pinned columns' drop shadows — a shadow
// shows only while there's content hidden past that edge. Measured off the
// scroll viewport, re-run on scroll, resize, and whenever the caller's deps
// (e.g. the visible column set) change table width. setState is guarded so we
// only re-render on an actual change, not per frame.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

export const useScrollEdges = (deps: unknown[] = []) => {
	const scrollRef = useRef<HTMLDivElement | null>(null)
	const [edge, setEdge] = useState<{ start: boolean; end: boolean }>({ start: true, end: true })
	const measure = useCallback(() => {
		const el = scrollRef.current
		if (!el) return
		const start = el.scrollLeft <= 0
		const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
		setEdge((prev) => (prev.start === start && prev.end === end ? prev : { start, end }))
	}, [])
	// Re-measure whenever the caller-supplied deps change (table width shifts).
	useLayoutEffect(() => {
		measure()
	}, [measure, ...deps])
	useEffect(() => {
		const el = scrollRef.current
		if (!el || typeof ResizeObserver === 'undefined') return
		const ro = new ResizeObserver(() => measure())
		ro.observe(el)
		return () => ro.disconnect()
	}, [measure])
	return { scrollRef, edge, measure }
}
